import { CloseRounded } from '@mui/icons-material';
import { IconButton, Popover, type PopoverOrigin } from '@mui/material';
import type { MouseEvent, ReactNode } from 'react';
import css from './PostListPopover.module.scss';

/**
 * The floating list of posts, the way Google Calendar floats a day: a header
 * with a close button over a column of rows. The calendar's "+N more" and the
 * compose page's "posted before" line share it, each bringing its own header
 * and rows (`PostChip`s). Open while it has an anchor.
 */
export function PostListPopover({
  anchorEl,
  onClose,
  anchorOrigin,
  transformOrigin,
  header,
  children,
}: {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  anchorOrigin: PopoverOrigin;
  transformOrigin: PopoverOrigin;
  header: ReactNode;
  children: ReactNode;
}) {
  return (
    <Popover
      open={anchorEl !== null}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      slotProps={{
        // React events bubble through the portal to whatever the anchor sits in
        // (the account card's fold header); a click on a row or on the backdrop
        // is the popover's own.
        root: { onClick: (e: MouseEvent<HTMLDivElement>) => e.stopPropagation() },
        paper: { className: css.paper },
      }}
    >
      <div className={css.body}>
        <div className={css.header}>
          {header}
          <IconButton className={css.close} aria-label="Close" onClick={onClose}>
            <CloseRounded />
          </IconButton>
        </div>
        <div className={css.list}>{children}</div>
      </div>
    </Popover>
  );
}
